import type { DodgeballGame, Ball, Explosion } from "./types.js";
import { CANVAS_W, CANVAS_H, WALL_TOP, WALL_BOTTOM, PLAYER_RADIUS, BALL_RADIUS, BALL_SPEED, MAX_HEALTH } from "./types.js";

export function createGame(): DodgeballGame {
  return {
    p1: { x: 40, y: CANVAS_H / 2, angle: 0, health: MAX_HEALTH, cooldown: 0 },
    p2: { x: CANVAS_W - 40, y: CANVAS_H / 2, angle: 180, health: MAX_HEALTH, cooldown: 0 },
    balls: [],
    frozenBalls: [],
    explosions: [],
    winner: null,
    message: `Dodgeball! ${MAX_HEALTH} lives each. Last one standing wins!`,
  };
}

export function setAngle(game: DodgeballGame, player: 1 | 2, delta: number): DodgeballGame {
  if (game.winner) return game;
  const p = player === 1 ? { ...game.p1 } : { ...game.p2 };
  p.angle = (p.angle + delta + 360) % 360;
  if (player === 1) return { ...game, p1: p };
  return { ...game, p2: p };
}

export function setAbsoluteAngle(game: DodgeballGame, player: 1 | 2, angle: number): DodgeballGame {
  if (game.winner) return game;
  const p = player === 1 ? { ...game.p1 } : { ...game.p2 };
  p.angle = ((angle % 360) + 360) % 360;
  if (player === 1) return { ...game, p1: p };
  return { ...game, p2: p };
}

const MOVE_SPEED = 180;

export function movePlayerVertical(game: DodgeballGame, player: 1 | 2, dir: -1 | 0 | 1): DodgeballGame {
  if (game.winner) return game;
  const p = player === 1 ? { ...game.p1 } : { ...game.p2 };
  p.y = Math.max(WALL_TOP + PLAYER_RADIUS + 4, Math.min(WALL_BOTTOM - PLAYER_RADIUS - 4, p.y + dir * MOVE_SPEED * 0.016));
  if (player === 1) return { ...game, p1: p };
  return { ...game, p2: p };
}

function muzzleVector(angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: Math.cos(rad), y: Math.sin(rad) };
}

export function shoot(game: DodgeballGame, player: 1 | 2): DodgeballGame {
  if (game.winner) return game;
  const p = player === 1 ? game.p1 : game.p2;
  if (p.cooldown > 0) return game;

  const dir = muzzleVector(p.angle);
  const spawnX = p.x + dir.x * (PLAYER_RADIUS + BALL_RADIUS + 4);
  const spawnY = p.y + dir.y * (PLAYER_RADIUS + BALL_RADIUS + 4);

  if (spawnY < WALL_TOP || spawnY > WALL_BOTTOM) return game;

  const ball: Ball = {
    x: spawnX, y: spawnY,
    vx: dir.x * BALL_SPEED,
    vy: dir.y * BALL_SPEED,
    radius: BALL_RADIUS,
  };

  const upd = player === 1
    ? { ...game, p1: { ...game.p1, cooldown: 20 } }
    : { ...game, p2: { ...game.p2, cooldown: 20 } };

  return { ...upd, balls: [...upd.balls, ball] };
}

export function tick(game: DodgeballGame, dt: number): DodgeballGame {
  if (game.winner) {
    const explosions = game.explosions
      .map((e) => ({ ...e, timer: e.timer - 1 }))
      .filter((e) => e.timer > 0);
    return { ...game, explosions };
  }
  const dtSec = Math.min(dt / 1000, 0.03);

  let { p1, p2 } = game;
  p1 = { ...p1, cooldown: Math.max(0, p1.cooldown - 1) };
  p2 = { ...p2, cooldown: Math.max(0, p2.cooldown - 1) };

  const balls: Ball[] = [];
  const frozenBalls: Ball[] = [];
  let explosions = game.explosions.map((e) => ({ ...e, timer: e.timer - 1 })).filter((e) => e.timer > 0);

  for (const b of game.balls) {
    let ball = { ...b };
    ball.x += ball.vx * dtSec;
    ball.y += ball.vy * dtSec;

    if (ball.y - ball.radius <= WALL_TOP) {
      ball.y = WALL_TOP + ball.radius;
      ball.vy = Math.abs(ball.vy) * 0.95;
    }
    if (ball.y + ball.radius >= WALL_BOTTOM) {
      ball.y = WALL_BOTTOM - ball.radius;
      ball.vy = -Math.abs(ball.vy) * 0.95;
    }

    if (ball.x < -50 || ball.x > CANVAS_W + 50) {
      continue;
    }

    const hitP1 = !playerHit(p1, ball);
    const hitP2 = !playerHit(p2, ball);

    if (hitP1) {
      p1 = { ...p1, health: p1.health - 1 };
      frozenBalls.push(ball);
      explosions.push({ x: p1.x, y: p1.y, timer: 60, maxTimer: 60 });
      continue;
    }
    if (hitP2) {
      p2 = { ...p2, health: p2.health - 1 };
      frozenBalls.push(ball);
      explosions.push({ x: p2.x, y: p2.y, timer: 60, maxTimer: 60 });
      continue;
    }

    balls.push(ball);
  }

  let winner: number | null = null;
  let message = game.message;
  if (p1.health <= 0) { winner = 2; message = "Player 2 wins!"; }
  if (p2.health <= 0) { winner = 1; message = "Player 1 wins!"; }

  return { p1, p2, balls, frozenBalls: [...game.frozenBalls, ...frozenBalls], explosions, winner, message };
}

function playerHit(p: { x: number; y: number; health: number }, ball: Ball): boolean {
  const dx = ball.x - p.x;
  const dy = ball.y - p.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return dist > PLAYER_RADIUS + ball.radius;
}

export function getAIMove(game: DodgeballGame): { shoot: boolean; angleDelta: number; wantsUp: boolean; wantsDown: boolean } {
  const me = game.p2;
  const target = game.p1;
  const dx = target.x - me.x;
  const dy = target.y - me.y;
  const targetAngle = Math.atan2(dy, dx) * (180 / Math.PI);
  let diff = targetAngle - me.angle;
  diff = ((diff + 180) % 360 + 360) % 360 - 180;

  let wantsUp = false;
  let wantsDown = false;

  const danger = game.balls.some((b) => {
    const ddx = b.x - me.x;
    const ddy = b.y - me.y;
    return Math.sqrt(ddx * ddx + ddy * ddy) < 150;
  });

  if (danger) {
    const dodgeUp = Math.random() < 0.5;
    wantsUp = dodgeUp;
    wantsDown = !dodgeUp;
    const dodgeAngle = (Math.random() < 0.5 ? -1 : 1) * 60;
    return { shoot: false, angleDelta: dodgeAngle * 0.5, wantsUp, wantsDown };
  }

  const threshold = 30 + Math.random() * 30;
  if (me.y < target.y - threshold) wantsDown = true;
  else if (me.y > target.y + threshold) wantsUp = true;
  else if (Math.random() < 0.02) { wantsUp = Math.random() < 0.5; wantsDown = !wantsUp; }

  const angleDelta = Math.sign(diff) * Math.min(Math.abs(diff) * 0.15, 3);
  const shoot = Math.abs(diff) < 10 && me.cooldown === 0 && Math.random() < 0.04;

  return { shoot, angleDelta, wantsUp, wantsDown };
}
